import { Result, IEvent, Ok, Err, IUser, IGroup, IPlace, EventAttendeeState, IConversation } from '@/classes'
import { fetchAPI } from '@/utils/useAPI'
import { IOrganization } from '~/classes/Organization'
import { IHomeModule } from '~/pages/app/index.vue'

export const eventAnnounceAPI = async (eventId: string) => {
	const response: Result<{ success: boolean }> = await fetchAPI('event/announce', { eventId }, { method: 'POST' })
	if (!response.ok) return response as Err

	return {
		ok: true,
		data: response.data
	} as Ok<{ success: boolean }>
}

export const eventAttendAPI = async (params: {
	event: string
	guests?: number
}) => {
	const response: Result<{ success: boolean, updatedEvent: IEvent }> = await fetchAPI('event/attend', params, { method: 'POST' })
	if (!response.ok) return response as Err

	const event = response.data.updatedEvent
	if (!event) return { ok: false, error: 'api-error' } as Err

	return {
		ok: true,
		data: { event }
	} as Ok<{ event: IEvent }>
}

export const eventAttendCancelAPI = async (params: {
	event: string
}) => {
	const response: Result<{ success: boolean, updatedEvent: IEvent }> = await fetchAPI('event/attend/cancel', params, { method: 'POST' })
	if (!response.ok) return response as Err

	const event = response.data.updatedEvent
	if (!event) return { ok: false, error: 'api-error' } as Err

	return {
		ok: true,
		data: { event }
	} as Ok<{ event: IEvent }>
}

export const eventAttendKickAPI = async (
	eventId: string,
	userId: string,
	params: {
		reasons: string[]
		details: string
	}
) => {
	const response: Result<{ success: boolean, updatedEvent: IEvent }> = await fetchAPI('event/attend/kick', { eventId, userId, params}, { method: 'POST' })

	return response
}

export const eventAttendSetAPI = async (
	eventId: string,
	targets: { [key: string ]: typeof EventAttendeeState[number] },
	options?: {
		validationCount?: number
	}
) => {
	const response: Result<{ success: boolean, updatedEvent: IEvent }> = await fetchAPI('event/attend/set', { event: eventId, targets, options }, { method: 'POST' })
	if (!response.ok) return response

	const event = response.data.updatedEvent 
	if (!event) return { ok: false, error: 'api-error' } as Err

	return {
		ok: true,
		data: { event }
	} as Ok<{ event: IEvent }>
}

export const eventRoleSetAPI = async (
	userId: string,
	eventId: string,
	role: 'organizer' | 'attendee'
) => {
	const response: Result<{ success: boolean, updatedEvent: IEvent }> = await fetchAPI('event/roles/set', { eventId, userId, role }, { method: 'POST' })

	if (!response.ok) return response

	const event = response.data.updatedEvent 
	if (!event) return { ok: false, error: 'api-error' } as Err

	return {
		ok: true,
		data: { event }
	} as Ok<{ event: IEvent }>
}

export const eventReviewAPI = async (
	eventId: string, params: {
		kudos: string[], tags: string[]
	}
) => {
	const response: Result<{ updatedEvent: IEvent }> = await fetchAPI('event/review', { eventId, params }, { method: 'POST' })

	if (!response.ok) return response

	const event = response.data.updatedEvent 
	if (!event) return { ok: false, error: 'api-error' } as Err

	return {
		ok: true,
		data: { event }
	} as Ok<{ event: IEvent }>
}

export const eventsGetAPI = async (params: {
	filters: {
    group?: string
		organization?: string
    isPast?: boolean
    isMine?: boolean
		categories?: string[]
		isOnlineOnly?: boolean
		isIncludePast?: boolean
		isPopular?: boolean
		isIncludeOnline?: boolean
    isNoDate?: boolean
    distance?: number
		maxDate?: Date
		coordinates?: [number, number]
  },
	options?: {
		page?: number
		per_page?: number
		sort?: { [key: string]: 1 | -1 }
		exclude?: string[]
		isEventsPremium?: boolean
		isLinked?: boolean
		isEventsNew?: boolean
		isEvents?: boolean
		isEventsScored?: boolean
		force?: boolean
	},
}, controller?: AbortController) => {
	const response: Result<{ events: IEvent[], eventsNew: IEvent[], eventsPremium: IEvent[], eventsScored: IEvent[], users: IUser[], vibesWeeks: VibesWeek[], organizations: IOrganization[] }> = await fetchAPI('event/get', params, { method: 'POST', controller })

	return response
}

export const eventGetOneAPI = async (params: {
	_id?: string,
	slug?: string
}) => {
	const response: Result<{
		event: IEvent | null,
		users: IUser[],
		conversation: IConversation | null,
		group: IGroup | null,
		place: IPlace | null
		organization: IOrganization | null
	}> = await fetchAPI('event/one', params, { method: 'POST' })

	if (!response.ok) return response

	return response
}

export const eventHomeAPI = async () => {
	const response: Result<{
		users: IUser[]
		organizations: IOrganization[]
		modules: IHomeModule[]
	}> = await fetchAPI('event/home', { }, { method: 'POST' })

	if (!response.ok) return response

	return response
}

export const eventGetRelatedAPI = async (
	eventId: string,
	options?: {
		page?: number
		per_page?: number
	}
) => {
	const response: Result<{ events: IEvent[] }> = await fetchAPI('event/related', { eventId, options }, { method: 'POST' })

	if (!response.ok) return response

	return response
}